/**
 * Modal for adding a new payer to the Auth Letter Payer Config.
 *
 * Asks for the payer name and lets the operator base the new payer on an
 * existing payer's prompt (copied on create) — or start blank.
 * @module AddPayerModal
 */
import { useState } from "react";

interface Props {
  existingPayers: string[];
  onCancel: () => void;
  onCreate: (name: string, copyFrom: string) => void;
}

const AddPayerModal = ({ existingPayers, onCancel, onCreate }: Props) => {
  const [name, setName] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Enter a payer name.");
      return;
    }
    if (existingPayers.some((p) => p.toLowerCase() === trimmed.toLowerCase())) {
      setError(`Payer "${trimmed}" already exists.`);
      return;
    }
    onCreate(trimmed, copyFrom);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        className="w-[420px] max-w-[92vw] rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-base font-bold text-primaryGray-1">Add Payer</div>

        <div className="mt-4 flex flex-col gap-1">
          <label className="text-xs font-semibold text-primaryGray-1">
            Payer name
          </label>
          <input
            autoFocus
            className="rounded border border-primaryGray-14 bg-white px-3 py-2 text-sm text-primaryGray-1 focus:border-primaryGray-1 focus:outline-none"
            placeholder="e.g. Aetna"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>

        <div className="mt-4 flex flex-col gap-1">
          <label className="text-xs font-semibold text-primaryGray-1">
            Start from an existing prompt
          </label>
          <select
            className="rounded border border-primaryGray-14 bg-white px-3 py-2 text-sm text-primaryGray-1 focus:border-primaryGray-1 focus:outline-none"
            value={copyFrom}
            onChange={(e) => setCopyFrom(e.target.value)}
          >
            <option value="">Blank (upload a prompt later)</option>
            {existingPayers.map((p) => (
              <option key={p} value={p}>
                {p.trim().toLowerCase() === "default" ? "Default" : p}
              </option>
            ))}
          </select>
          <span className="text-tiny text-primaryGray-6">
            The selected payer's prompt is copied into the new payer. You can
            change it anytime by uploading a new JSON.
          </span>
        </div>

        {error && <div className="mt-3 text-xs text-red-500">{error}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded border border-primaryGray-14 px-3 py-2 text-tiny font-semibold text-primaryGray-1 hover:bg-primaryGray-16"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="rounded border border-gray-800 bg-gray-800 px-4 py-2 text-tiny font-semibold text-white hover:bg-gray-700"
          >
            Add Payer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPayerModal;
